import React from 'react';
import {
  Target,
  ChartBar,
  FileText,
  ArrowRight,
  CircleDollarSign,
  Users,
  Building2,
  GitBranch,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Workflow
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && (
      <div className="text-sm text-gray-500 mt-1">{trend}</div>
    )}
  </div>
);

const UemoaInteropAnalysis = () => {
  // Contexte et Objectifs
  const projectContext = {
    vision: "Système d'interopérabilité financière régional pour l'UEMOA",
    objectives: [
      "Faciliter les transactions transfrontalières",
      "Réduire les coûts de transaction de 60%",
      "Atteindre une inclusion financière de 75%",
      "Standardiser les échanges interbancaires"
    ],
    scope: {
      geographical: ["8 pays UEMOA"],
      institutions: [
        "130+ banques commerciales",
        "75+ institutions de microfinance",
        "15+ fintech agréées"
      ],
      services: [
        "Paiements instantanés P2P",
        "Transferts interbancaires",
        "Compensation multilatérale",
        "Settlement temps réel"
      ]
    }
  };

  // Analyse Marché & Impact
  const marketAnalysis = {
    marketSize: {
      volume: "2.5M transactions/jour",
      value: "850M€ volume quotidien",
      growth: "+45% croissance annuelle"
    },
    stakeholders: [
      {
        type: "Régulateurs",
        entities: ["BCEAO", "Commission UEMOA"],
        expectations: ["Stabilité système", "Conformité réglementaire", "Reporting temps réel"]
      },
      {
        type: "Institutions financières",
        entities: ["Banques", "IMF", "EME"],
        expectations: ["Coûts réduits", "Intégration simple", "Support 24/7"]
      }
    ],
    impact: {
      financial: [
        "Réduction coûts de 60%",
        "ROI estimé < 18 mois",
        "Économies d'échelle"
      ],
      social: [
        "Inclusion financière +25%",
        "Accès services x3",
        "Couverture rurale +40%"
      ]
    }
  };

  // Analyse Technique
  const technicalAnalysis = {
    architecture: {
      components: [
        {
          name: "Switch Central",
          description: "Routage et processing transactions",
          tech: ["Java/Spring", "Kafka", "Redis"],
          status: "En développement"
        },
        {
          name: "Settlement Engine",
          description: "Compensation et règlement",
          tech: ["Golang", "PostgreSQL", "RabbitMQ"],
          status: "POC validé"
        },
        {
          name: "API Gateway",
          description: "Interface participant",
          tech: ["Node.js", "Kong", "OAuth 2.0"],
          status: "En test"
        }
      ],
      standards: [
        {
          name: "ISO 20022",
          scope: "Messages financiers",
          status: "Adopté"
        },
        {
          name: "ISO 8583",
          scope: "Legacy systems",
          status: "Support maintenu"
        }
      ]
    },
    challenges: [
      {
        category: "Performance",
        points: [
          "Latence < 3s bout en bout",
          "Disponibilité 99.99%",
          "Scalabilité 5000 TPS"
        ],
        solutions: [
          "Architecture distribuée",
          "Caching multi-niveaux",
          "Load balancing adaptatif"
        ]
      },
      {
        category: "Sécurité",
        points: [
          "Chiffrement E2E",
          "Authentification forte",
          "Audit trail complet"
        ],
        solutions: [
          "PKI régionale",
          "HSM clusters",
          "Blockchain pour audit"
        ]
      }
    ]
  };

  // Plan d'implémentation
  const implementationPlan = {
    phases: [
      {
        name: "Phase 1 - Fondation",
        timeline: "Q1-Q2 2024",
        deliverables: [
          "Switch central v1",
          "APIs participantes",
          "Portail monitoring"
        ],
        status: "En cours"
      },
      {
        name: "Phase 2 - Extension",
        timeline: "Q3-Q4 2024",
        deliverables: [
          "Settlement temps réel",
          "Support ISO 20022",
          "Dashboard analytics"
        ],
        status: "Planifié"
      },
      {
        name: "Phase 3 - Innovation",
        timeline: "2025",
        deliverables: [
          "Instant payments",
          "Open Banking APIs",
          "ML fraud detection"
        ],
        status: "Prévu"
      }
    ],
    risks: [
      {
        risk: "Adoption participants",
        impact: "Élevé",
        mitigation: "Programme onboarding structuré"
      },
      {
        risk: "Performance système",
        impact: "Moyen",
        mitigation: "Tests charge continus"
      },
      {
        risk: "Conformité régulateur",
        impact: "Élevé",
        mitigation: "Validation continue BCEAO"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Analyse Détaillée - Projet Interopérabilité UEMOA
          </h1>
          <div className="flex space-x-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: Développement
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Budget: 127M€
            </span>
          </div>
        </div>

        {/* Vision et Contexte */}
        <Section title="Vision et Contexte" icon={Target}>
          <div className="space-y-6">
            <AnalysisPoint title="Vision Stratégique">
              <p>{projectContext.vision}</p>
              <div className="mt-4">
                <h4 className="font-medium mb-2">Objectifs Clés:</h4>
                <ul className="list-disc pl-4 space-y-1">
                  {projectContext.objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            </AnalysisPoint>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="font-medium mb-2">Couverture Géographique</h4>
                <ul className="space-y-1">
                  {projectContext.scope.geographical.map((geo, idx) => (
                    <li key={idx} className="flex items-center">
                      <Globe className="w-4 h-4 mr-2 text-blue-500" />
                      {geo}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Institutions</h4>
                <ul className="space-y-1">
                  {projectContext.scope.institutions.map((inst, idx) => (
                    <li key={idx} className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                      {inst}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Services</h4>
                <ul className="space-y-1">
                  {projectContext.scope.services.map((service, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* Analyse Marché */}
        <Section title="Analyse Marché & Impact" icon={ChartBar}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Metric
                label="Volume Transactions"
                value={marketAnalysis.marketSize.volume}
                trend="Croissance continue"
              />
              <Metric
                label="Volume Financier"
                value={marketAnalysis.marketSize.value}
                trend="Base quotidienne"
              />
              <Metric
                label="Croissance"
                value={marketAnalysis.marketSize.growth}
                trend="Projection 2024"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {marketAnalysis.stakeholders.map((stake, idx) => (
                <AnalysisPoint 
                  key={idx} 
                  title={stake.type}
                  status="info"
                >
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-sm font-medium mb-1">Entités:</h4>
                      <ul className="space-y-1">
                        {stake.entities.map((entity, i) => (
                          <li key={i} className="flex items-center">
                            <Users className="w-4 h-4 mr-2 text-blue-500" />
                            {entity}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-1">Attentes:</h4>
                      <ul className="space-y-1">
                        {stake.expectations.map((exp, i) => (
                          <li key={i} className="flex items-center">
                            <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                            {exp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AnalysisPoint>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AnalysisPoint 
                title="Impact Financier" 
                status="success"
              >
                <ul className="space-y-1">
                  {marketAnalysis.impact.financial.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <CircleDollarSign className="w-4 h-4 mr-2 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </AnalysisPoint>

              <AnalysisPoint 
                title="Impact Social" 
                status="success"
              >
                <ul className="space-y-1">
                  {marketAnalysis.impact.social.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <Users className="w-4 h-4 mr-2 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </AnalysisPoint>
            </div>
          </div>
        </Section>

        {/* Analyse Technique */}
        <Section title="Analyse Technique" icon={GitBranch}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {technicalAnalysis.architecture.components.map((comp, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">{comp.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{comp.description}</p>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-xs font-medium mb-1">Technologies:</h4>
                      <div className="flex flex-wrap gap-2">
                        {comp.tech.map((t, i) => (
                                                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                              {t}
                            </span>
                          ))}
                        </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium mb-1">Statut:</h4>
                      <span className={`px-2 py-1 rounded text-xs ${
                        comp.status.includes('En développement') 
                          ? 'bg-yellow-100 text-yellow-700'
                          : comp.status.includes('POC') 
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                      }`}>
                        {comp.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalAnalysis.challenges.map((challenge, idx) => (
                <AnalysisPoint 
                  key={idx} 
                  title={challenge.category}
                  status="warning"
                >
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium mb-1">Exigences:</h4>
                      <ul className="space-y-1">
                        {challenge.points.map((point, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-1">Solutions:</h4>
                      <ul className="space-y-1">
                        {challenge.solutions.map((solution, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            {solution}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AnalysisPoint>
              ))}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-3">Standards & Protocoles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {technicalAnalysis.architecture.standards.map((standard, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <span className="font-medium">{standard.name}</span>
                      <p className="text-sm text-gray-600">{standard.scope}</p>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      {standard.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Plan d'Implémentation */}
        <Section title="Plan d'Implémentation" icon={Workflow}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {implementationPlan.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.status === 'En cours' 
                        ? 'bg-blue-100 text-blue-700'
                        : phase.status === 'Planifié'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Timeline: {phase.timeline}</p>
                  <div>
                    <h4 className="text-sm font-medium mb-2">Livrables:</h4>
                    <ul className="space-y-1">
                      {phase.deliverables.map((deliverable, i) => (
                        <li key={i} className="flex items-center text-sm">
                          <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <AnalysisPoint title="Gestion des Risques" status="warning">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {implementationPlan.risks.map((risk, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{risk.risk}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        risk.impact === 'Élevé' 
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        Impact: {risk.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Mitigation: {risk.mitigation}
                    </p>
                  </div>
                ))}
              </div>
            </AnalysisPoint>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default UemoaInteropAnalysis;